import { useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { track, getSource } from "@/lib/track";
import { toast } from "sonner";
import { btnPrimary, btnOutline, inputCls, labelCls } from "@/components/kit";
import CommuneField from "@/components/CommuneField";
import { ArrowRight, ArrowLeft, Paperclip, Check } from "lucide-react";

const STEPS = ["Votre événement", "Où et quand ?", "Dimension du projet", "Prestations", "Coordonnées"];

const EVENT_TYPES = ["Inauguration", "Soirée d'entreprise", "Séminaire", "Lancement de produit", "Noël d'entreprise", "Congrès ou salon", "Autre"];
const BUDGETS = ["Moins de 5 000 €", "5 000 à 10 000 €", "10 000 à 25 000 €", "25 000 à 50 000 €", "Plus de 50 000 €", "Budget à définir"];
const ORG_TYPES = ["Entreprise", "CSE", "Collectivité", "Association"];
const PRESTATIONS = ["Organisation complète", "Lieu", "Traiteur", "Bar mobile", "Hôtesses", "Technique", "Mobilier et décoration", "Animations", "Sécurité", "Nettoyage", "Photographie et vidéo"];
const LOCATION_MODES = ["Dans nos locaux", "Recherche d'un lieu", "Les deux / à conseiller"];
const SCHEDULES = ["Journée complète", "Matin", "Après-midi", "Soirée", "À définir"];
const DECISION_DELAYS = ["Le plus tôt possible", "Sous 1 mois", "1 à 3 mois", "Plus de 3 mois"];
const CALLBACK_TIMES = ["Matin (9h-12h)", "Après-midi (14h-18h)", "Soirée (18h-20h)"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^(?:\+33|0)\s?[1-9](?:[\s.\-]?\d{2}){4}$/;

const slugId = (s) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

function Pills({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid} role="group">
      {options.map((o) => {
        const active = value === o;
        return (
          <button type="button" key={o} aria-pressed={active} onClick={() => onChange(o)}
            data-testid={`${testid}-${slugId(o)}`}
            className={`rounded-full border px-4 py-2.5 text-sm transition-colors ${active ? "border-lime bg-lime text-ink" : "border-ink/15 bg-white text-ink/60 hover:border-lime-dim hover:text-ink"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

function MultiPills({ options, values, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid} role="group">
      {options.map((o) => {
        const active = values.includes(o);
        return (
          <button type="button" key={o} aria-pressed={active}
            onClick={() => onChange(active ? values.filter((v) => v !== o) : [...values, o])}
            data-testid={`${testid}-${slugId(o)}`}
            className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm transition-colors ${active ? "border-lime bg-lime text-ink" : "border-ink/15 bg-white text-ink/60 hover:border-lime-dim hover:text-ink"}`}>
            {active && <Check size={13} />}{o}
          </button>
        );
      })}
    </div>
  );
}

export default function BriefForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefillVille = params.get("ville") || "";
  const prefillCode = params.get("code") || "";

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const startedRef = useRef(false);
  const [form, setForm] = useState({
    service: "", city: prefillVille, city_code: prefillCode, overseas: null,
    postal_code: "", location_mode: "", event_date: "", date_period: "", schedule: "",
    participants: "", budget: "", org_type: "", decision_delay: "",
    prestations: [], name: "", company: "", email: "", phone: "",
    best_callback_time: "", message: "", brief_file_url: "",
  });

  const set = (patch) => {
    if (!startedRef.current) { startedRef.current = true; track("form_start"); }
    setForm((f) => ({ ...f, ...patch }));
  };

  const validateStep = (s) => {
    if (s === 1 && !form.service) return "Choisissez un type d'événement.";
    if (s === 2 && !form.city.trim()) return "Indiquez la ville de votre événement.";
    if (s === 5) {
      if (!form.name.trim()) return "Votre nom est requis.";
      if (!EMAIL_RE.test(form.email)) return "Un email professionnel valide est requis.";
      if (!PHONE_RE.test(form.phone.trim())) return "Un téléphone valide est requis (ex : 06 12 34 56 78).";
    }
    return null;
  };

  const next = () => {
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    track("form_step", `etape_${step}_terminee`);
    setStep((s) => Math.min(5, s + 1));
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const uploadBrief = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
    const allowed = [".pdf", ".doc", ".docx", ".odt", ".txt", ".rtf", ".xls", ".xlsx", ".ppt", ".pptx", ".zip"];
    if (!allowed.includes(ext)) { toast.error("Format non supporté (PDF, Word, Excel, PowerPoint, texte, zip)."); e.target.value = ""; return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Fichier trop lourd (15 Mo max)."); e.target.value = ""; return; }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/upload/brief", data);
      set({ brief_file_url: res.url });
      toast.success("Cahier des charges joint.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const submit = async () => {
    const err = validateStep(5);
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const payload = { type: "devis", ...form, source: getSource() };
      if (payload.overseas === null) delete payload.overseas;
      const { data } = await api.post("/submissions", payload);
      track("form_submit");
      navigate(`/merci-devis?id=${data.id}`, { state: { summary: data } });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const progress = useMemo(() => (step / 5) * 100, [step]);

  return (
    <div data-testid="brief-form">
      {/* Progression */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs text-ink/50">
          <span data-testid="brief-step-label">Étape {step} sur 5 — {STEPS[step - 1]}</span>
          <span>{Math.round(progress)} %</span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-ink/10" data-testid="brief-progress">
          <div className="h-full rounded-full bg-lime transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Étape 1 — type d'événement */}
      {step === 1 && (
        <div data-testid="brief-step-1">
          <p className={labelCls}>Quel type d'événement organisez-vous ? <span className="text-lime-dim">*</span></p>
          <Pills options={EVENT_TYPES} value={form.service} onChange={(v) => set({ service: v })} testid="brief-service" />
        </div>
      )}

      {/* Étape 2 — où et quand */}
      {step === 2 && (
        <div className="space-y-7" data-testid="brief-step-2">
          <div>
            <label htmlFor="brief-city" className={labelCls}>Ville de l'événement <span className="text-lime-dim">*</span></label>
            <CommuneField id="brief-city" value={prefillVille} defaultCode={prefillCode} required
              placeholder="Recherchez votre commune — ex : Valence, Crest, Lyon…"
              onSelect={(v) => set(v)} />
          </div>
          <div className="grid gap-7 sm:grid-cols-2">
            <div>
              <label htmlFor="brief-postal-code" className={labelCls}>Code postal (optionnel)</label>
              <input id="brief-postal-code" type="text" inputMode="numeric" maxLength={5} className={inputCls} data-testid="brief-postal-code"
                value={form.postal_code} placeholder="Ex : 26000" onChange={(e) => set({ postal_code: e.target.value })} />
            </div>
            <div>
              <label htmlFor="brief-schedule" className={labelCls}>Horaires prévisionnels</label>
              <select id="brief-schedule" className={`${inputCls} appearance-none`} data-testid="brief-schedule"
                value={form.schedule} onChange={(e) => set({ schedule: e.target.value })}>
                <option value="">Sélectionnez…</option>
                {SCHEDULES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <p className={labelCls}>Où se déroulera l'événement ?</p>
            <Pills options={LOCATION_MODES} value={form.location_mode} onChange={(v) => set({ location_mode: v })} testid="brief-location-mode" />
          </div>
          <div className="grid gap-7 sm:grid-cols-2">
            <div>
              <label htmlFor="brief-event-date" className={labelCls}>Date souhaitée</label>
              <input id="brief-event-date" type="date" className={inputCls} data-testid="brief-event-date"
                value={form.event_date} onChange={(e) => set({ event_date: e.target.value })} />
            </div>
            <div>
              <label htmlFor="brief-date-period" className={labelCls}>Ou période envisagée</label>
              <input id="brief-date-period" type="text" className={inputCls} data-testid="brief-date-period"
                value={form.date_period} placeholder="Ex : mi-décembre, hors week-end" onChange={(e) => set({ date_period: e.target.value })} />
            </div>
          </div>
        </div>
      )}

      {/* Étape 3 — dimension */}
      {step === 3 && (
        <div className="space-y-7" data-testid="brief-step-3">
          <div className="grid gap-7 sm:grid-cols-2">
            <div>
              <label htmlFor="brief-participants" className={labelCls}>Nombre d'invités estimé</label>
              <input id="brief-participants" type="number" min="1" className={inputCls} data-testid="brief-participants"
                value={form.participants} placeholder="Ex : 150" onChange={(e) => set({ participants: e.target.value })} />
            </div>
            <div>
              <label htmlFor="brief-decision-delay" className={labelCls}>Votre délai de décision</label>
              <select id="brief-decision-delay" className={`${inputCls} appearance-none`} data-testid="brief-decision-delay"
                value={form.decision_delay} onChange={(e) => set({ decision_delay: e.target.value })}>
                <option value="">Sélectionnez…</option>
                {DECISION_DELAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div>
            <p className={labelCls}>Budget disponible</p>
            <Pills options={BUDGETS} value={form.budget} onChange={(v) => set({ budget: v })} testid="brief-budget" />
          </div>
          <div>
            <p className={labelCls}>Vous organisez pour</p>
            <Pills options={ORG_TYPES} value={form.org_type} onChange={(v) => set({ org_type: v })} testid="brief-org-type" />
          </div>
        </div>
      )}

      {/* Étape 4 — prestations */}
      {step === 4 && (
        <div data-testid="brief-step-4">
          <p className={labelCls}>Quelles prestations recherchez-vous ?</p>
          <MultiPills options={PRESTATIONS} values={form.prestations} onChange={(v) => set({ prestations: v })} testid="brief-prestations" />
          <p className="mt-3 text-xs text-ink/45">« Organisation complète » = nous pilotons tout, du lieu au démontage. Vous pouvez aussi ne cocher que les postes utiles.</p>
        </div>
      )}

      {/* Étape 5 — coordonnées */}
      {step === 5 && (
        <div className="space-y-7" data-testid="brief-step-5">
          <div className="grid gap-7 sm:grid-cols-2">
            <div>
              <label htmlFor="brief-name" className={labelCls}>Nom complet <span className="text-lime-dim">*</span></label>
              <input id="brief-name" type="text" required className={inputCls} data-testid="brief-name"
                value={form.name} placeholder="Jean Dupont" onChange={(e) => set({ name: e.target.value })} />
            </div>
            <div>
              <label htmlFor="brief-company" className={labelCls}>Société</label>
              <input id="brief-company" type="text" className={inputCls} data-testid="brief-company"
                value={form.company} placeholder="Votre entreprise" onChange={(e) => set({ company: e.target.value })} />
            </div>
            <div>
              <label htmlFor="brief-email" className={labelCls}>Email professionnel <span className="text-lime-dim">*</span></label>
              <input id="brief-email" type="email" required className={inputCls} data-testid="brief-email"
                value={form.email} placeholder="jean@entreprise.com" onChange={(e) => set({ email: e.target.value })} />
            </div>
            <div>
              <label htmlFor="brief-phone" className={labelCls}>Téléphone <span className="text-lime-dim">*</span></label>
              <input id="brief-phone" type="tel" required className={inputCls} data-testid="brief-phone"
                value={form.phone} placeholder="06 12 34 56 78" onChange={(e) => set({ phone: e.target.value })} />
            </div>
          </div>
          <div>
            <p className={labelCls}>Meilleur horaire pour vous rappeler</p>
            <Pills options={CALLBACK_TIMES} value={form.best_callback_time} onChange={(v) => set({ best_callback_time: v })} testid="brief-callback-time" />
          </div>
          <div>
            <label htmlFor="brief-message" className={labelCls}>Votre projet en quelques mots (optionnel)</label>
            <textarea id="brief-message" rows={4} className={inputCls} data-testid="brief-message"
              value={form.message} placeholder="Contexte, objectifs, contraintes particulières…" onChange={(e) => set({ message: e.target.value })} />
          </div>
          <div>
            <p className={labelCls}>Cahier des charges (optionnel)</p>
            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-ink/20 bg-white px-5 py-4 text-sm text-ink/60 transition-colors hover:border-lime-dim" data-testid="brief-file-drop">
              <Paperclip size={16} className="text-lime-dim" />
              {uploading ? "Envoi en cours…" : form.brief_file_url ? "Fichier joint ✓ — cliquez pour le remplacer" : "Joindre un fichier (PDF, Word, Excel — 15 Mo max)"}
              <input type="file" className="hidden" accept=".pdf,.doc,.docx,.odt,.txt,.rtf,.xls,.xlsx,.ppt,.pptx,.zip"
                data-testid="brief-file-input" onChange={uploadBrief} disabled={uploading} />
            </label>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="mt-10 flex items-center justify-between gap-4">
        {step > 1 ? (
          <button type="button" onClick={prev} className={btnOutline} data-testid="brief-prev"><ArrowLeft size={16} /> Retour</button>
        ) : <span />}
        {step < 5 ? (
          <button type="button" onClick={next} className={btnPrimary} data-testid="brief-next">Continuer <ArrowRight size={16} /></button>
        ) : (
          <button type="button" onClick={submit} disabled={loading} className={`${btnPrimary} disabled:opacity-60`} data-testid="brief-submit">
            {loading ? "Envoi…" : "Recevoir ma première estimation"} <ArrowRight size={16} />
          </button>
        )}
      </div>
      {step === 5 && (
        <p className="mt-4 text-xs leading-relaxed text-ink/45" data-testid="brief-privacy">
          En envoyant ce brief, vous acceptez que vos informations soient utilisées uniquement pour traiter votre demande.
          Vous disposez d'un droit d'accès, de rectification et de suppression — voir nos mentions légales.
        </p>
      )}
    </div>
  );
}
